import { useEffect, useState } from "react";
import { Mail, MessageSquare, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import Dialog from "@/components/billing/Dialog";
import InventorySearchDialog from "@/components/billing/InventorySearchDialog";
import { fmtPrice, lineTotalWithVat, roundFinalPriceUp } from "@/lib/totals";

const OFFER_DISCOUNT = 20;

/** Iter 169 — Segundo modal: artículos del inventario con 20 % dto → CONFIRMAR OFERTA → envío Email/WhatsApp. */
export default function ChecklistOfferModal({ repairId, incidents, onClose, onDone }) {
  const [parts, setParts] = useState([]);
  const [lines, setLines] = useState([]);
  const [search, setSearch] = useState(false);
  const [offer, setOffer] = useState(null); // tras CONFIRMAR OFERTA
  const [busy, setBusy] = useState(false);
  const [wa, setWa] = useState(null); // { body, wa_link }

  useEffect(() => { api.get("/parts").then((r) => setParts(r.data || [])).catch(() => {}); }, []);

  const addPicked = (picked) => {
    setLines((prev) => {
      const next = [...prev];
      picked.forEach((pk) => {
        const p = parts.find((x) => x.id === pk.product_id);
        if (!p) return;
        const i = next.findIndex((l) => l.part_id === p.id);
        if (i >= 0) next[i] = { ...next[i], quantity: next[i].quantity + pk.quantity };
        else next.push({ part_id: p.id, quantity: pk.quantity, part: p });
      });
      return next;
    });
  };

  const total = lines.reduce((s, l) => s + lineTotalWithVat(l.quantity, l.part.sale_price, OFFER_DISCOUNT), 0);

  const confirmOffer = async () => {
    if (!lines.length) return toast.error("Añade al menos un artículo.");
    setBusy(true);
    try {
      const r = await api.post(`/repairs/${repairId}/checklist-offers`, {
        incident_keys: incidents.map((i) => i.key),
        lines: lines.map((l) => ({ part_id: l.part_id, quantity: l.quantity })),
      });
      setOffer(r.data);
      toast.success("Oferta preparada. Elige cómo enviarla al cliente.");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const send = async (channel, overrideText) => {
    setBusy(true);
    try {
      const r = await api.post(`/repairs/${repairId}/checklist-offers/${offer.id}/send`, {
        channel, override_text: overrideText ?? null, preview: channel === "whatsapp" && overrideText === undefined,
      });
      if (channel === "email") {
        toast.success(`Oferta enviada por email a ${r.data.sent_to}`);
        onDone?.(); onClose();
      } else {
        setWa(r.data);
        if (overrideText !== undefined) { window.open(r.data.wa_link, "_blank", "noopener"); onDone?.(); onClose(); }
      }
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const incidentText = incidents.map((i) => i.label).join(", ");

  return (
    <Dialog onClose={onClose} title={offer ? "Enviar oferta al cliente" : "Oferta por incidencia de Checklist"} wide>
      <div data-testid="checklist-offer-modal" className="space-y-4">
        <div className="border-l-3 border border-[#E5E5E0] bg-[#FDEDE7] px-3 py-2 text-sm" data-testid="offer-incident">
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#9B2C1F]">Incidencia detectada</span>
          <div className="font-semibold">{incidentText}</div>
        </div>

        {!offer ? (
          <>
            <div className="flex items-center justify-between">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Productos / Servicios · {OFFER_DISCOUNT} % de descuento automático</div>
              <button type="button" onClick={() => setSearch(true)} data-testid="offer-add-items"
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs uppercase tracking-wider bg-[#111110] text-white">
                <Plus size={12} /> Añadir artículos
              </button>
            </div>
            {lines.length === 0 ? (
              <div className="text-sm text-[#525252] border border-dashed border-[#E5E5E0] p-6 text-center" data-testid="offer-empty">Sin artículos todavía.</div>
            ) : (
              <table className="w-full text-sm" data-testid="offer-lines">
                <thead><tr className="text-left font-mono text-[10px] uppercase tracking-widest text-[#525252] border-b border-[#E5E5E0]">
                  <th className="py-2">Artículo</th><th>Cant.</th><th>Garantía</th><th>Stock</th><th className="text-right">Precio normal</th><th className="text-right">Dto</th><th className="text-right">Precio oferta</th><th></th>
                </tr></thead>
                <tbody>
                  {lines.map((l) => {
                    const p = l.part; const normal = roundFinalPriceUp(l.quantity * (Number(p.sale_price) || 0));
                    const offerPrice = lineTotalWithVat(l.quantity, p.sale_price, OFFER_DISCOUNT);
                    return (
                      <tr key={l.part_id} className="border-b border-[#E5E5E0]" data-testid={`offer-line-${p.sku || p.id}`}>
                        <td className="py-2"><div className="font-semibold">{p.name}</div><div className="font-mono text-[10px] text-[#525252]">{p.sku} · {p.kind === "service" ? "Servicio" : "Producto"}{p.stock_source === "master" ? " · Artículo maestro" : ""}</div></td>
                        <td className="font-mono">{l.quantity}</td>
                        <td className="text-xs">{Number(p.warranty_months) ? `${p.warranty_months} meses` : "Sin garantía"}</td>
                        <td className="font-mono text-xs">{p.kind === "service" ? "—" : (p.stock ?? "—")}</td>
                        <td className="text-right font-mono line-through text-[#8A8A82]">{fmtPrice(normal)}</td>
                        <td className="text-right font-mono text-[#6E8B3D]">−{OFFER_DISCOUNT} %</td>
                        <td className="text-right font-mono font-bold" data-testid={`offer-line-price-${p.sku || p.id}`}>{fmtPrice(offerPrice)}</td>
                        <td className="text-right"><button type="button" onClick={() => setLines(lines.filter((x) => x.part_id !== l.part_id))} className="p-1 text-[#9B2C1F]"><Trash2 size={13} /></button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            <div className="flex items-center justify-between border-t-2 border-[#111110] pt-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Importe oferta (IVA incluido)</div>
              <div className="font-display text-2xl font-black" data-testid="offer-total">{fmtPrice(total)}</div>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-wider border border-[#E5E5E0]">Cancelar</button>
              <button type="button" onClick={confirmOffer} disabled={busy || !lines.length} data-testid="offer-confirm"
                className="px-5 py-2 text-xs uppercase tracking-wider bg-[#6E8B3D] text-white font-bold disabled:bg-[#A8A8A0]">
                {busy ? "Guardando…" : "Confirmar oferta"}
              </button>
            </div>
            <InventorySearchDialog open={search} onClose={() => setSearch(false)} parts={parts} onAdd={addPicked} />
          </>
        ) : (
          <>
            <div className="text-sm" data-testid="offer-summary">
              {offer.lines.map((l) => (
                <div key={l.id} className="flex justify-between border-b border-[#E5E5E0] py-1.5">
                  <span>{l.name}{l.quantity > 1 ? ` × ${l.quantity}` : ""}</span>
                  <span className="font-mono"><s className="text-[#8A8A82] mr-2">{fmtPrice(l.regular_total)}</s><b>{fmtPrice(l.offer_price)}</b></span>
                </div>
              ))}
              <div className="flex justify-between pt-2 font-bold"><span>Importe oferta</span><span className="font-mono" data-testid="offer-saved-total">{fmtPrice(offer.total)}</span></div>
            </div>
            <p className="text-xs text-[#525252]">La oferta se incluye en el enlace del presupuesto existente. Elige el canal; no se envía nada hasta que lo confirmes.</p>
            {!wa ? (
              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={() => send("email")} disabled={busy} data-testid="offer-send-email"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 text-xs uppercase tracking-wider bg-[#6E8B3D] text-white font-semibold disabled:bg-[#A8A8A0]">
                  <Mail size={14} /> Enviar por email
                </button>
                <button type="button" onClick={() => send("whatsapp")} disabled={busy} data-testid="offer-send-whatsapp"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 text-xs uppercase tracking-wider bg-[#25D366] text-white font-semibold disabled:bg-[#A8A8A0]">
                  <MessageSquare size={14} /> Enviar por WhatsApp
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Mensaje de WhatsApp (editable, idioma del cliente)</label>
                <textarea value={wa.body} onChange={(e) => setWa({ ...wa, body: e.target.value })} rows={8} data-testid="offer-wa-text"
                  className="w-full ds-card outline-none px-3 py-2 text-sm rounded-none font-mono" />
                <div className="flex justify-end">
                  <button type="button" onClick={() => send("whatsapp", wa.body)} disabled={busy} data-testid="offer-wa-open"
                    className="px-4 py-2 text-xs uppercase tracking-wider bg-[#25D366] text-white font-semibold disabled:bg-[#A8A8A0]">Abrir WhatsApp</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}
